
import img0 from './DownloadConfig.png';
import img1 from './DownloadEncryptionConfig.png';
  
const content = `# Database Download Guide
## Objective
***
Both the validated database backups and the encrypted database backups can be downloaded from OnData. The downloaded databases can be restored in another environment.
The objective of the document is to guide OnData customers on how to download validated database backups and encrypted database backups. Please note that only validated
database backups and successfully encrypted database backups will be made available for download.

Besides transferring the backup securely with HTTPS, two additional security mechanisms are used to provide more layers of security for database download. First, multi-factor
authentication is used to ensure the person who downloads the database is an authorized user. Second, the database backups are fully encrypted before transfer. The encrypted
backups can be decrypted once all files are successfully downloaded from OnData.

**Note:** As the data egress from the cloud platforms are not free, the database download activities will incur a cost on the data being transferred. The actual cost is proportional
to the amount of data being downloaded from OnData.

***
## Document information
***
Document Version: 1.1  
Published Date: 09/04/2022  

***
## Download Validated Database Backups
***
Follow the steps below to download a validated database backup from OnData.
* Download the DownloadConfig.ini file for the database backup from the "Backup and Validation Details" page of the OnData Portal.

  ![Config File for Download](${ img0 } 'Config File for Download')

* Download the PowerShell script "DownloadBackup.ps1" from the following link.

  [PowerShell Script for Downloading Backups](download_res#DownloadBackup/DownloadBackup.ps1 'PowerShell Script for Downloading Backups')

* Copy the "DownloadConfig.ini" file and the PowerShell script "DownloadBackup.ps1" to a machine that has access to the Internet.

  **NOTE:** Place the "DownloadConfig.ini" file and the "DownloadBackup.ps1" file in **the same folder**. Also, please make sure you have enough free disk space that is at
  least twice as large as the size of the full database backup.

* Launch a PowerShell command line with super user permission. Use sudo for Ubuntu and su for Redhat Linux and CentOS. Execute the DownloadBackup.ps1 file to download the backup.

  \`\`\`PowerShell
  $ sudo pwsh
  PS /home/user> ./DownloadBackup.ps1
  \`\`\`

* You will be prompted to perform multi-factor authentication. Enter the one-time passcode received from SMS or email to start the download.

  **NOTE:** If the downloaded database backup is not a full database backup, you may need to download the corresponding full backup and all incremental backups in between
  in order to restore the database.

***
## Download Encrypted Database Backups
***
Follow the steps below to download an encrypted database backup from OnData.
* Download the DownloadEncryptionConfig.ini file for the encrypted database backup from the "Encryption Details" page of the OnData Portal.

  ![Config File for Download](${ img1 } 'Config File for Download')

* Download the PowerShell script "DownloadEncryption.ps1" from the following link.

  [PowerShell Script for Downloading Encrypted Backups](download_res#DownloadEncryption/DownloadEncryption.ps1 'PowerShell Script for Downloading Encrypted Backups')

* Copy the "DownloadEncryptionConfig.ini" file and the PowerShell script "DownloadEncryption.ps1" to a machine that has access to the Internet.

  **NOTE:** Place the "DownloadEncryptionConfig.ini" file and the "DownloadEncryption.ps1" file in **the same folder**. Also, please make sure you have enough free disk
  space that is at least twice as large as the size of the encrypted database backup.

* Launch a PowerShell command line with super user permission. Use sudo for Ubuntu and su for Redhat Linux and CentOS. Execute the DownloadEncryption.ps1 file to
  download the backup.

  \`\`\`PowerShell
  $ sudo pwsh
  PS /home/user> ./DownloadEncryption.ps1
  \`\`\`

* You will be prompted to perform multi-factor authentication. Enter the one-time passcode received from SMS or email to start the download. The encrypted database can be
  restored after the download. To connect to the encrypted database with dynamic encryption and decryption enabled, you will need to download and install the OnData
  Drivers. For more details about the OnData Drivers, please refer to the corresponding documentation under the Drivers topic.`
  
export default content;
